import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, onSnapshot, addDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, appId, storage } from '../lib/firebase';
import { runMigration, createInvitation } from '../lib/invitations';
import { 
  User, Building2, Save, Upload, FileText, Loader2, Check, Users, UserPlus, 
  Link as LinkIcon, Copy, Shield, Trash2, X, CreditCard, Zap, AlertTriangle, 
  Crown, CheckCircle2, Landmark, Scale, Mail, Wrench, Lock 
} from 'lucide-react';
import Button from './ui/Button';

const ROLES = {
  admin: { 
    id: 'admin',
    label: 'Admin', 
    desc: 'Accès total : Paramètres, Banque, Devis, Factures, Équipe.', 
    color: 'bg-purple-100 text-purple-700 border-purple-200' 
  },
  owner: { 
    id: 'owner',
    label: 'Propriétaire', 
    desc: 'Accès total et droits de propriété sur l\'entreprise.', 
    color: 'bg-amber-100 text-amber-700 border-amber-200' 
  },
  manager: { 
    id: 'manager',
    label: 'Chef d\'équipe', 
    desc: 'Peut créer, modifier et envoyer les Devis & Factures.', 
    color: 'bg-blue-100 text-blue-700 border-blue-200' 
  },
  editor: { 
    id: 'editor',
    label: 'Éditeur (ancien)', 
    desc: 'Ancienne version - utilisez Chef d\'équipe.', 
    color: 'bg-blue-100 text-blue-700 border-blue-200' 
  },
  member: { 
    id: 'member',
    label: 'Membre', 
    desc: 'Lecture/écriture sur les chantiers et documents.', 
    color: 'bg-green-100 text-green-700 border-green-200' 
  },
  viewer: { 
    id: 'viewer',
    label: 'Observateur', 
    desc: 'Lecture seule : Accès au planning et aux documents de chantier.', 
    color: 'bg-slate-100 text-slate-700 border-slate-200' 
  }
};

const PLANS = {
    monthly: { name: 'Pro Mensuel', price: 10, features: ['Devis Illimités', 'Facturation', 'Multi-utilisateurs'] },
    yearly: { name: 'Pro Annuel', price: 96, features: ['Devis Illimités', 'Facturation', 'Multi-utilisateurs', '-20% de remise'] },
    business: { name: 'Business (Chantiers)', price: 15, features: ['Tout du plan Pro', 'Suivi de Chantier', 'Photos illimitées', 'Planning Équipe'], highlight: true }
};

const Settings = ({ user, activeCompanyId, activeCompanyName }) => {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);

  // États Données
  const [companyForm, setCompanyForm] = useState({ 
      name: 'BatiFlow', address: '', city: '', phone: '', email: '', 
      siret: '', vatNumber: '', defaultVat: 20, logoUrl: '', signatureUrl: '',
      legalFooter: 'Dispensé d\'immatriculation au registre du commerce et des sociétés (RCS) et au répertoire des métiers (RM)',
      cgv: '',
      iban: '', bic: '', bankName: '',
      paymentMethods: { virement: true, cheque: false, especes: false, cb: false },
      quoteValidityDays: 30,
      paymentTermDays: 30,
      latePenaltyText: 'En cas de retard de paiement, pénalités égales à 3 fois le taux d\'intérêt légal + indemnité forfaitaire de recouvrement de 40€ (Art. L441-6 du Code de Commerce).',
      defaultExecutionDelay: '',
      insuranceName: '', insurancePolicy: '', insuranceArea: 'France Métropolitaine',
      defaultDownPaymentValue: 30, 
      defaultDownPaymentType: 'percent'
  });
  
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', role: 'Gérant', photoUrl: '' });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [subscription, setSubscription] = useState({ plan: 'free', status: 'inactive' });
  const [loadingSub, setLoadingSub] = useState(false);
  
  // États Invitation
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteSent, setInviteSent] = useState(false);
  
  // --- CORRECTION : Variable indispensable pour éviter le crash ---
  const [inviteLink, setInviteLink] = useState(''); 

  useEffect(() => {
    if (!user) return;
    
    const loadSettings = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'general'));
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.company) setCompanyForm(prev => ({ ...prev, ...data.company }));
          if (data.profile) setProfileForm(prev => ({ ...prev, ...data.profile }));
        }
        
        // Charger le plan de l'ENTREPRISE (pas du user individuel)
        if (activeCompanyId) {
          const companySnap = await getDoc(doc(db, 'companies', activeCompanyId));
          if(companySnap.exists()) {
              const companyData = companySnap.data();
              if(companyData.plan) setSubscription({ plan: companyData.plan, status: companyData.subscriptionStatus || 'active' });
          }
        }
      } catch (error) { console.error(error); }
    };
    loadSettings();

    // Charger les membres de l'entreprise active depuis Firestore
    let unsubTeam = null;
    if (activeCompanyId) {
      unsubTeam = onSnapshot(collection(db, 'companies', activeCompanyId, 'members'), (snapshot) => {
        const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Trier par hiérarchie : owner > admin > manager > member > viewer
        const roleOrder = { owner: 1, admin: 2, manager: 3, editor: 3, member: 4, viewer: 5 };
        members.sort((a, b) => (roleOrder[a.role] || 99) - (roleOrder[b.role] || 99));
        
        setTeamMembers(members);
        
        // Trouver le rôle de l'utilisateur actuel
        const currentMember = members.find(m => m.uid === user.uid);
        setCurrentUserRole(currentMember?.role || null);
      });
    }

    return () => { if (unsubTeam) unsubTeam(); };
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'general'), { company: companyForm, profile: profileForm, updatedAt: new Date().toISOString() }, { merge: true });
    setSuccess(true); setTimeout(() => setSuccess(false), 3000); setLoading(false);
  };

  const handleChangePlan = async (newPlanFullId) => {
      if(!window.confirm(`Confirmer le changement d'offre pour cette entreprise ?`)) return;
      setLoadingSub(true);
      try {
          // Mettre à jour l'abonnement pour l'ENTREPRISE (important pour que tous les membres aient accès)
          if (activeCompanyId) {
              console.log('[Settings] Updating company plan:', newPlanFullId, 'for company:', activeCompanyId);
              await setDoc(doc(db, 'companies', activeCompanyId), { 
                  plan: newPlanFullId, 
                  subscriptionStatus: 'active',
                  planUpdatedAt: new Date().toISOString()
              }, { merge: true });
              console.log('[Settings] Company plan updated successfully');
          }
          
          // Aussi mettre à jour le plan utilisateur pour les données historiques
          if (user.uid && !user.isAnonymous && !user.uid.startsWith('demo')) {
              console.log('[Settings] Updating user plan:', newPlanFullId);
              await setDoc(doc(db, 'users', user.uid), { 
                  plan: newPlanFullId, 
                  subscriptionStatus: 'active' 
              }, { merge: true });
          }
          
          setSubscription({ plan: newPlanFullId, status: 'active' });
          setLoadingSub(false);
          alert('Votre abonnement a été mis à jour avec succès ! Tous les membres de l\'équipe ont maintenant accès.');
      } catch (error) {
          console.error('[Settings] Error updating plan:', error);
          setLoadingSub(false);
          alert('Erreur lors de la mise à jour de l\'abonnement.');
      }
  };

  const handleCancelSubscription = async () => {
      if(!window.confirm("Êtes-vous sûr de vouloir résilier ?")) return;
      setLoadingSub(true);
      setTimeout(async () => {
          if (user.uid && !user.isAnonymous && !user.uid.startsWith('demo')) {
              await updateDoc(doc(db, 'users', user.uid), { subscriptionStatus: 'canceled' });
          }
          setSubscription(prev => ({ ...prev, status: 'canceled' }));
          setLoadingSub(false);
      }, 1000);
  };

  // --- FONCTION UPLOAD PHOTO DE PROFIL ---
  const handlePhotoUpload = async (file) => {
    if (!file || !user) return;
    
    // Vérifier que c'est bien une image
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image (JPG, PNG, etc.)');
      return;
    }
    
    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La photo doit faire moins de 5 MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      // Créer une référence dans Firebase Storage
      const photoRef = ref(storage, `profile-photos/${user.uid}/${Date.now()}_${file.name}`);
      
      // Uploader le fichier
      await uploadBytes(photoRef, file);
      
      // Obtenir l'URL de téléchargement
      const photoURL = await getDownloadURL(photoRef);
      
      // Mettre à jour le state
      setProfileForm({ ...profileForm, photoUrl: photoURL });
      
      // Sauvegarder automatiquement
      await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'general'), {
        profile: { ...profileForm, photoUrl: photoURL }
      }, { merge: true });
      
    } catch (error) {
      console.error('Erreur upload photo:', error);
      alert('Erreur lors de l\'upload de la photo');
    }
    setUploadingPhoto(false);
  };

  // --- FONCTION MIGRATION ---
  const handleMigration = async () => {
    if (!window.confirm('Êtes-vous sûr ? Cette action va ajouter companyId aux documents existants.')) {
      return;
    }
    
    setIsMigrating(true);
    try {
      const result = await runMigration(appId);
      setMigrationResult(result);
      console.log('Migration result:', result);
    } catch (error) {
      console.error('Migration error:', error);
      setMigrationResult({ success: false, error: error.message });
    }
    setIsMigrating(false);
  };

  // --- FONCTION D'ENVOI D'EMAIL ---
  const sendInvite = async () => {
      if (!inviteEmail || !inviteEmail.includes('@')) {
          alert("Veuillez entrer une adresse email valide.");
          return;
      }

      setLoading(true);
      try {
                    // Create a persistent invitation document that the Cloud Function will send
                    if (!activeCompanyId) throw new Error('Aucune entreprise active sélectionnée.');
                    const tokenId = await createInvitation({ companyId: activeCompanyId, companyName: companyForm.name || null, email: inviteEmail, role: inviteRole, inviterUid: user.uid });

                    const magicLink = `https://www.batiflow.net/join?token=${tokenId}`;

                    // 1. Keep a local pending entry for the team's UI
                    await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'team'), {
                            email: inviteEmail,
                            role: inviteRole,
                            status: 'pending',
                            name: inviteEmail.split('@')[0],
                            invitedAt: new Date().toISOString(),
                            token: tokenId
                    });

                    // The Cloud Function `sendInvitationEmail` will trigger on the invitations doc creation
                    // and send the real email. We still keep the local 'team' pending entry for UI continuity.

          setInviteSent(true);
          setTimeout(() => {
              setInviteSent(false);
              setInviteEmail('');
              setShowInviteModal(false);
          }, 2000);
      } catch (error) {
          console.error("Erreur invitation:", error);
          alert("Erreur lors de l'envoi de l'invitation. Vérifiez vos droits d'accès.");
      }
      setLoading(false);
  };

  const generateInvite = (r) => { 
      const token = Math.random().toString(36).substring(7);
      setInviteLink(`https://www.batiflow.net/join?t=${token}&r=${r}`);
  };

  const handleRemoveMember = async (memberId) => { 
    if (!window.confirm("Retirer ce membre de l'équipe ?")) return;
    if (!activeCompanyId) {
      alert("Aucune entreprise active sélectionnée.");
      return;
    }
    try {
      await deleteDoc(doc(db, 'companies', activeCompanyId, 'members', memberId));
      // Optionally, also remove from users/{memberId}.companyIds
      const userRef = doc(db, 'users', memberId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const companyIds = (userData.companyIds || []).filter(id => id !== activeCompanyId);
        await setDoc(userRef, { companyIds }, { merge: true });
      }
    } catch (err) {
      console.error("Erreur lors du retrait du membre:", err);
      alert("Erreur lors du retrait du membre.");
    }
  };
  const handleLogoUpload = (e) => { const file = e.target.files[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => { setCompanyForm({ ...companyForm, logoUrl: reader.result }); }; reader.readAsDataURL(file); } };

  const isPlanActive = (planId) => subscription.plan === planId && subscription.status === 'active';
  const isBusinessActive = subscription.plan?.includes('business') && subscription.status === 'active';

  return (
    <div className="max-w-4xl mx-auto p-6 animate-page pb-24">
      <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">Paramètres</h2>

      <div className="flex gap-2 mb-8 border-b border-slate-200 overflow-x-auto scrollbar-hide">
        {[
            { id: 'company', label: 'Entreprise', icon: Building2 },
            { id: 'profile', label: 'Profil', icon: User },
            { id: 'team', label: 'Équipe', icon: Users },
            { id: 'subscription', label: 'Abonnement', icon: CreditCard },
        ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-3 px-4 font-medium text-sm flex items-center gap-2 transition-colors relative whitespace-nowrap ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
              <tab.icon size={18} /> {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>}
            </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
        
        {/* --- ONGLET ENTREPRISE --- */}
        {activeTab === 'company' && (
          <div className="space-y-8 animate-page">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="text-blue-600" size={24} />
                <div>
                  <p className="text-xs font-bold text-blue-600 uppercase">Entreprise active</p>
                  <p className="text-lg font-bold text-slate-800">{activeCompanyName}</p>
                </div>
              </div>
              {currentUserRole && (
                <div className="flex items-center gap-2">
                  <Shield size={16} className="text-slate-500" />
                  <span className={`text-xs px-3 py-1.5 rounded-lg border font-bold ${ROLES[currentUserRole]?.color || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                    {ROLES[currentUserRole]?.label || currentUserRole}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex gap-4">
                <div className="w-32 h-32 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-blue-400 relative group">
                  {companyForm.logoUrl ? <img src={companyForm.logoUrl} className="w-full h-full object-contain p-2" alt="Logo"/> : <><Upload size={24} className="mb-2"/><span className="text-xs font-bold">Logo</span></>}
                  <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleLogoUpload} accept="image/*"/>
                </div>
                <div className="w-32 h-32 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-green-400 relative group">
                  {companyForm.signatureUrl ? <img src={companyForm.signatureUrl} className="w-full h-full object-contain p-2" alt="Signature"/> : <><Upload size={24} className="mb-2"/><span className="text-xs font-bold text-center">Signature</span></>}
                  <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setCompanyForm({...companyForm, signatureUrl: reader.result});
                      };
                      reader.readAsDataURL(file);
                    }
                  }} accept="image/*"/>
                </div>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                <input className="border rounded-lg p-2 w-full" placeholder="Nom Entreprise" value={companyForm.name} onChange={e=>setCompanyForm({...companyForm, name:e.target.value})} />
                <input className="border rounded-lg p-2 w-full" placeholder="SIRET" value={companyForm.siret} onChange={e=>setCompanyForm({...companyForm, siret:e.target.value})} />
                <input className="border rounded-lg p-2 w-full md:col-span-2" placeholder="Adresse" value={companyForm.address} onChange={e=>setCompanyForm({...companyForm, address:e.target.value})} />
                <input className="border rounded-lg p-2 w-full md:col-span-2" placeholder="Ville & CP" value={companyForm.city} onChange={e=>setCompanyForm({...companyForm, city:e.target.value})} />
              </div>
            </div>

            {/* Sections réservées aux Admin/Owner */}
            {(currentUserRole === 'admin' || currentUserRole === 'owner') ? (
            <>
            <div>
                <h3 className="text-sm font-bold text-slate-800 uppercase mb-4 flex items-center gap-2"><Shield size={16} className="text-blue-600"/> Assurance & Juridique</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <div className="md:col-span-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Nom de l'assureur</label>
                        <input className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ex: MAAF Pro" value={companyForm.insuranceName} onChange={e => setCompanyForm({...companyForm, insuranceName: e.target.value})} />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Numéro de contrat</label>
                        <input className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="N° Police" value={companyForm.insurancePolicy} onChange={e => setCompanyForm({...companyForm, insurancePolicy: e.target.value})} />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Zone de couverture</label>
                        <input className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="France" value={companyForm.insuranceArea} onChange={e => setCompanyForm({...companyForm, insuranceArea: e.target.value})} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Pied de page légal</label>
                        <textarea rows="2" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" value={companyForm.legalFooter} onChange={e => setCompanyForm({...companyForm, legalFooter: e.target.value})} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block flex items-center gap-2"><Scale size={12}/> Conditions Générales de Vente (CGV)</label>
                        <textarea rows="8" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-blue-500 font-mono text-slate-600" value={companyForm.cgv} onChange={e => setCompanyForm({...companyForm, cgv: e.target.value})} placeholder="Vos CGV ici..." />
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-sm font-bold text-slate-800 uppercase mb-4 flex items-center gap-2"><Landmark size={16} className="text-blue-600"/> Banque & Préférences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                     <div className="md:col-span-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Nom de la Banque</label>
                        <input className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ex: Crédit Agricole" value={companyForm.bankName} onChange={e => setCompanyForm({...companyForm, bankName: e.target.value})} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">IBAN</label>
                        <input className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none font-mono text-sm focus:ring-2 focus:ring-blue-500" placeholder="FR76 ..." value={companyForm.iban} onChange={e => setCompanyForm({...companyForm, iban: e.target.value})} />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">BIC</label>
                        <input className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none font-mono text-sm focus:ring-2 focus:ring-blue-500" value={companyForm.bic} onChange={e => setCompanyForm({...companyForm, bic: e.target.value})} />
                    </div>
                    
                    {/* MÉTHODES DE PAIEMENT */}
                    <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Méthodes de paiement acceptées</label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer bg-white p-3 rounded-lg border border-slate-200 hover:border-blue-400 transition">
                                <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" checked={companyForm.paymentMethods?.virement || false} onChange={e => setCompanyForm({...companyForm, paymentMethods: {...(companyForm.paymentMethods || {}), virement: e.target.checked}})} />
                                <span className="text-sm text-slate-700">Virement bancaire</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer bg-white p-3 rounded-lg border border-slate-200 hover:border-blue-400 transition">
                                <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" checked={companyForm.paymentMethods?.cheque || false} onChange={e => setCompanyForm({...companyForm, paymentMethods: {...(companyForm.paymentMethods || {}), cheque: e.target.checked}})} />
                                <span className="text-sm text-slate-700">Chèque</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer bg-white p-3 rounded-lg border border-slate-200 hover:border-blue-400 transition">
                                <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" checked={companyForm.paymentMethods?.especes || false} onChange={e => setCompanyForm({...companyForm, paymentMethods: {...(companyForm.paymentMethods || {}), especes: e.target.checked}})} />
                                <span className="text-sm text-slate-700">Espèces</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer bg-white p-3 rounded-lg border border-slate-200 hover:border-blue-400 transition">
                                <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" checked={companyForm.paymentMethods?.cb || false} onChange={e => setCompanyForm({...companyForm, paymentMethods: {...(companyForm.paymentMethods || {}), cb: e.target.checked}})} />
                                <span className="text-sm text-slate-700">Carte bancaire</span>
                            </label>
                        </div>
                    </div>
                    
                    {/* RÉGLAGES PAR DÉFAUT */}
                     <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">TVA par défaut (%)</label>
                        <input type="number" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" value={companyForm.defaultVat} onChange={e => setCompanyForm({...companyForm, defaultVat: Number(e.target.value)})} />
                     </div>
                     <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2">
                        <label className="text-[10px] font-bold text-blue-600 uppercase mb-1 block flex items-center gap-2"><Zap size={12}/> Acompte par défaut</label>
                        <div className="flex gap-2">
                            <input 
                                type="number" 
                                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none" 
                                placeholder="30" 
                                value={companyForm.defaultDownPaymentValue} 
                                onChange={e => setCompanyForm({...companyForm, defaultDownPaymentValue: Number(e.target.value)})} 
                            />
                            <select 
                                className="bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none text-sm font-bold text-slate-600"
                                value={companyForm.defaultDownPaymentType || 'percent'}
                                onChange={e => setCompanyForm({...companyForm, defaultDownPaymentType: e.target.value})}
                            >
                                <option value="percent">%</option>
                                <option value="fixed">€</option>
                            </select>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">Sera appliqué automatiquement à chaque nouveau devis.</p>
                     </div>
                </div>
            </div>
            
            {/* MENTIONS LÉGALES ET DÉLAIS */}
            <div>
                <h3 className="text-sm font-bold text-slate-800 uppercase mb-4 flex items-center gap-2"><Scale size={16} className="text-blue-600"/> Mentions légales</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Validité des devis (jours)</label>
                        <input type="number" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="30" value={companyForm.quoteValidityDays || 30} onChange={e => setCompanyForm({...companyForm, quoteValidityDays: Number(e.target.value)})} />
                        <p className="text-[10px] text-slate-400 mt-1">Ex: "Devis valable 30 jours"</p>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Délai de paiement (jours)</label>
                        <input type="number" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="30" value={companyForm.paymentTermDays || 30} onChange={e => setCompanyForm({...companyForm, paymentTermDays: Number(e.target.value)})} />
                        <p className="text-[10px] text-slate-400 mt-1">Délai max pour régler la facture</p>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Délai d'exécution par défaut</label>
                        <input className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ex: 3 semaines" value={companyForm.defaultExecutionDelay || ''} onChange={e => setCompanyForm({...companyForm, defaultExecutionDelay: e.target.value})} />
                        <p className="text-[10px] text-slate-400 mt-1">Durée estimée des travaux</p>
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">Pénalités de retard (obligatoire)</label>
                        <textarea rows="3" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-blue-500" value={companyForm.latePenaltyText || ''} onChange={e => setCompanyForm({...companyForm, latePenaltyText: e.target.value})} />
                        <p className="text-[10px] text-slate-400 mt-1">Mention légale obligatoire depuis 2013</p>
                    </div>
                </div>
            </div>
            </>
            ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
                <Lock size={40} className="mx-auto mb-3 text-slate-400"/>
                <h3 className="text-sm font-bold text-slate-700 mb-1">Informations réservées</h3>
                <p className="text-xs text-slate-500">Ces informations sensibles (banque, assurance, juridique) ne sont visibles que par les administrateurs et propriétaires de l'entreprise.</p>
            </div>
            )}

            <div className="pt-6 border-t border-slate-100 flex justify-end gap-3">
                {success && <span className="text-green-600 font-bold flex items-center gap-2 animate-in fade-in"><Check size={18}/> Sauvegardé !</span>}
                <Button variant="primary" onClick={handleSave} disabled={loading}>{loading ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>} Enregistrer</Button>
            </div>
          </div>
        )}

        {/* --- ONGLET ABONNEMENT --- */}
        {activeTab === 'subscription' && (
            <div className="animate-page space-y-10">
                <div className="relative overflow-hidden rounded-2xl p-8 text-white shadow-xl">
                    <div className={`absolute inset-0 ${subscription.status === 'active' ? 'bg-gradient-to-r from-blue-600 to-indigo-700' : 'bg-gradient-to-r from-slate-700 to-slate-800'}`}></div>
                    <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                        <div>
                            <h3 className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1">Abonnement Actuel</h3>
                            <div className="text-3xl font-extrabold flex items-center gap-3">
                                {PLANS[subscription.plan.split('_')[0]]?.name || 'Gratuit'}
                                <span className={`px-3 py-1 text-[10px] rounded-full font-bold uppercase tracking-wide border ${subscription.status === 'active' ? 'bg-green-500/20 border-green-400/30 text-green-100' : 'bg-red-500/20 border-red-400/30 text-red-100'}`}>
                                    {subscription.status === 'active' ? 'Actif' : 'Inactif'}
                                </span>
                            </div>
                        </div>
                        
                        {subscription.status === 'active' && (
                            <button onClick={handleCancelSubscription} disabled={loadingSub} className="px-5 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-sm font-medium transition backdrop-blur-sm flex items-center gap-2">
                                {loadingSub ? <Loader2 size={16} className="animate-spin"/> : <X size={16}/>} Résilier
                            </button>
                        )}
                    </div>
                </div>

                <div>
                    <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-2 text-xl">
                        <Zap size={24} className="text-yellow-500 fill-yellow-500"/> Choisissez votre formule
                    </h4>
                    <p className="text-slate-500 mb-8">Des tarifs adaptés à votre activité. Changez à tout moment.</p>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* PLAN PRO */}
                        <div className="space-y-4">
                            <div className="bg-white rounded-2xl border-2 border-slate-200 p-8 hover:border-blue-300 hover:shadow-lg transition">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="p-3 rounded-xl bg-blue-50">
                                        <Building2 size={28} className="text-blue-600"/>
                                    </div>
                                    {isPlanActive('pro_monthly') || isPlanActive('pro_yearly') ? (
                                        <span className="bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1.5 rounded-full">ACTUEL</span>
                                    ) : null}
                                </div>
                                
                                <h3 className="text-2xl font-bold text-slate-900 mb-2">Artisan Pro</h3>
                                <p className="text-slate-600 text-sm mb-6">L'essentiel pour gérer votre activité</p>
                                
                                <div className="space-y-3 mb-6">
                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <Check size={18} className="text-blue-500 flex-shrink-0"/>
                                        <span>Devis & Factures <strong>illimités</strong></span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <Check size={18} className="text-blue-500 flex-shrink-0"/>
                                        <span>Clients & Catalogue</span>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100 pt-6 space-y-3">
                                    <div className="flex items-baseline justify-between">
                                        <div>
                                            <span className="text-3xl font-extrabold text-slate-900">10€</span>
                                            <span className="text-slate-500 text-sm ml-1">/mois</span>
                                        </div>
                                        {isPlanActive('pro_monthly') ? (
                                            <button disabled className="px-4 py-2 rounded-lg bg-blue-50 text-blue-600 text-sm font-bold cursor-default">Actif</button>
                                        ) : (
                                            <button onClick={() => handleChangePlan('pro_monthly')} disabled={loadingSub} className="px-4 py-2 rounded-lg border-2 border-slate-200 text-slate-700 text-sm font-bold hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition">Choisir</button>
                                        )}
                                    </div>
                                    
                                    <div className="flex items-baseline justify-between">
                                        <div>
                                            <span className="text-3xl font-extrabold text-slate-900">96€</span>
                                            <span className="text-slate-500 text-sm ml-1">/an</span>
                                            <span className="ml-2 text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded">-20%</span>
                                        </div>
                                        {isPlanActive('pro_yearly') ? (
                                            <button disabled className="px-4 py-2 rounded-lg bg-blue-50 text-blue-600 text-sm font-bold cursor-default">Actif</button>
                                        ) : (
                                            <button onClick={() => handleChangePlan('pro_yearly')} disabled={loadingSub} className="px-4 py-2 rounded-lg border-2 border-slate-200 text-slate-700 text-sm font-bold hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition">Choisir</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PLAN BUSINESS - PREMIUM DESIGN */}
                        <div className="space-y-4 md:-translate-y-4">
                            <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border-2 border-orange-500/50 p-8 shadow-2xl overflow-hidden">
                                {/* Effet de brillance */}
                                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-3xl"></div>
                                
                                <div className="absolute top-4 right-4">
                                    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-lg animate-pulse">
                                        ⚡ Recommandé
                                    </div>
                                </div>
                                
                                <div className="relative z-10">
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 shadow-lg">
                                            <Crown size={28} className="text-white"/>
                                        </div>
                                        {isPlanActive('business_monthly') || isPlanActive('business_yearly') ? (
                                            <span className="bg-green-500/20 border border-green-400/30 text-green-100 text-xs font-bold px-3 py-1.5 rounded-full">ACTUEL</span>
                                        ) : null}
                                    </div>
                                    
                                    <h3 className="text-2xl font-bold text-white mb-2">Business</h3>
                                    <p className="text-slate-300 text-sm mb-6">Tout ce qu'il faut pour développer votre activité</p>
                                    
                                    <div className="space-y-3 mb-6">
                                        <div className="flex items-center gap-2 text-sm text-white font-medium">
                                            <Check size={18} className="text-orange-400 flex-shrink-0"/>
                                            <span>Devis & Factures <strong>illimités</strong></span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-200">
                                            <Check size={18} className="text-orange-400 flex-shrink-0"/>
                                            <span>Clients & Catalogue</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-200">
                                            <Check size={18} className="text-orange-400 flex-shrink-0"/>
                                            <span><strong className="text-white">Suivi de Chantier</strong> complet</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-200">
                                            <Check size={18} className="text-orange-400 flex-shrink-0"/>
                                            <span>Photos & Documents <strong className="text-white">illimités</strong></span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-200">
                                            <Check size={18} className="text-orange-400 flex-shrink-0"/>
                                            <span>Planning Équipe avancé</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-200">
                                            <Check size={18} className="text-orange-400 flex-shrink-0"/>
                                            <span>Création d'entreprise</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-200">
                                            <Check size={18} className="text-orange-400 flex-shrink-0"/>
                                            <span>Membres <strong className="text-white">illimités</strong></span>
                                        </div>
                                    </div>

                                    <div className="border-t border-white/10 pt-6 space-y-3">
                                        <div className="flex items-baseline justify-between">
                                            <div>
                                                <span className="text-4xl font-extrabold text-white">15€</span>
                                                <span className="text-slate-300 text-sm ml-1">/mois</span>
                                            </div>
                                            {isPlanActive('business_monthly') ? (
                                                <button disabled className="px-5 py-2.5 rounded-lg bg-white/20 text-white text-sm font-bold cursor-default backdrop-blur">Actif</button>
                                            ) : (
                                                <button onClick={() => handleChangePlan('business_monthly')} disabled={loadingSub} className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 text-white text-sm font-bold hover:from-orange-600 hover:to-red-700 shadow-lg hover:shadow-xl transition transform hover:scale-105">
                                                    {loadingSub ? <Loader2 className="animate-spin" size={16}/> : 'Passer à Business'}
                                                </button>
                                            )}
                                        </div>
                                        
                                        <div className="flex items-baseline justify-between">
                                            <div>
                                                <span className="text-4xl font-extrabold text-white">144€</span>
                                                <span className="text-slate-300 text-sm ml-1">/an</span>
                                                <span className="ml-2 text-xs font-bold text-green-400 bg-green-500/20 px-2 py-1 rounded border border-green-400/30">-20%</span>
                                            </div>
                                            {isPlanActive('business_yearly') ? (
                                                <button disabled className="px-5 py-2.5 rounded-lg bg-white/20 text-white text-sm font-bold cursor-default backdrop-blur">Actif</button>
                                            ) : (
                                                <button onClick={() => handleChangePlan('business_yearly')} disabled={loadingSub} className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 text-white text-sm font-bold hover:from-orange-600 hover:to-red-700 shadow-lg hover:shadow-xl transition transform hover:scale-105">
                                                    {loadingSub ? <Loader2 className="animate-spin" size={16}/> : 'Passer à Business'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-orange-100">
                                    <AlertTriangle size={20} className="text-orange-600"/>
                                </div>
                                <p className="text-sm text-orange-900 font-medium">Le plan Business inclut tout ce dont vous avez besoin pour gérer vos chantiers professionnellement.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* --- ONGLET EQUIPE --- */}
        {activeTab === 'team' && (
            <div className="animate-page space-y-8">
                <div className="flex justify-between items-center p-4 bg-slate-900 text-white rounded-xl">
                    <div><h3 className="font-bold text-lg">Gérer mon équipe</h3><p className="text-slate-400 text-sm">Invitez vos collaborateurs par email.</p></div>
                    {subscription.plan?.includes('business') ? (
                      <Button variant="blue" icon={UserPlus} onClick={() => setShowInviteModal(true)}>Inviter</Button>
                    ) : (
                      <div className="text-sm text-slate-400 italic flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-lg border border-slate-700">
                        <Lock size={16} />
                        Business uniquement
                      </div>
                    )}
                </div>
                <div className="grid gap-4">
                    {teamMembers.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Users size={48} className="mx-auto mb-3 opacity-30" />
                        <p className="font-medium">Aucun membre pour le moment</p>
                      </div>
                    ) : (
                      teamMembers.map(member => {
                        const isCurrentUser = member.uid === user.uid;
                        const memberName = member.name || member.email?.split('@')[0] || 'Membre';
                        const initial = memberName.charAt(0).toUpperCase();
                        
                        return (
                          <div key={member.id} className={`flex items-center justify-between p-4 border rounded-xl transition ${isCurrentUser ? 'border-blue-300 bg-blue-50/50 ring-1 ring-blue-200' : 'border-slate-200 bg-slate-50'}`}>
                              <div className="flex items-center gap-4">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border ${isCurrentUser ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-slate-700 border-slate-200'}`}>
                                    {initial}
                                  </div>
                                  <div>
                                      <div className="font-bold text-slate-800 flex items-center gap-2">
                                        {memberName}
                                        {isCurrentUser && <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full font-bold">VOUS</span>}
                                      </div>
                                      <div className="text-xs text-slate-500 flex items-center gap-1">
                                          <Mail size={10}/> {member.email}
                                      </div>
                                  </div>
                              </div>
                              <div className="flex items-center gap-4">
                                  <span className={`text-xs px-3 py-1.5 rounded-lg border font-bold ${ROLES[member.role]?.color || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                                    {ROLES[member.role]?.label || member.role}
                                  </span>
                                  {!isCurrentUser && (currentUserRole === 'admin' || currentUserRole === 'owner') && (
                                    <button onClick={() => handleRemoveMember(member.id)} className="text-slate-400 hover:text-red-500 transition">
                                      <Trash2 size={18} />
                                    </button>
                                  )}
                              </div>
                          </div>
                        );
                      })
                    )}
                </div>
            </div>
        )}

        {/* --- ONGLET PROFIL --- */}
        {activeTab === 'profile' && (
          <div className="space-y-8 animate-page max-w-2xl">
             <div className="flex items-center gap-6">
                <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-white shadow-lg flex items-center justify-center overflow-hidden relative group cursor-pointer">
                    {uploadingPhoto ? (
                      <Loader2 className="animate-spin text-slate-400" size={32} />
                    ) : profileForm.photoUrl ? (
                      <img src={profileForm.photoUrl} alt="Profil" className="w-full h-full object-cover" />
                    ) : (
                      <User size={40} className="text-slate-300" />
                    )}
                    {!uploadingPhoto && (
                      <>
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                          <Upload size={20} className="text-white" />
                        </div>
                        <input 
                          type="file" 
                          accept="image/*"
                          className="absolute inset-0 opacity-0 cursor-pointer" 
                          onChange={(e) => {if(e.target.files[0]) handlePhotoUpload(e.target.files[0])}}
                        />
                      </>
                    )}
                </div>
                <div>
                  <h3 className="font-bold text-xl text-slate-800">{profileForm.firstName || 'Prénom'} {profileForm.lastName || 'Nom'}</h3>
                  <p className="text-slate-500">{profileForm.role}</p>
                  <p className="text-xs text-slate-400 mt-1">Cliquez sur la photo pour la changer</p>
                </div>
             </div>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input className="border rounded-lg p-2" value={profileForm.firstName} onChange={e=>setProfileForm({...profileForm, firstName:e.target.value})} placeholder="Prénom"/>
                <input className="border rounded-lg p-2" value={profileForm.lastName} onChange={e=>setProfileForm({...profileForm, lastName:e.target.value})} placeholder="Nom"/>
                <input className="border rounded-lg p-2" value={profileForm.role} onChange={e=>setProfileForm({...profileForm, role:e.target.value})} placeholder="Rôle"/>
             </div>
             <div className="pt-6 border-t border-slate-100 flex justify-end gap-3">
                {success && <span className="text-green-600 font-bold flex items-center gap-2 animate-in fade-in"><Check size={18}/> Sauvegardé !</span>}
                <Button variant="primary" onClick={handleSave} disabled={loading}>{loading ? <Loader2 className="animate-spin" size={18}/> : <Save size={18}/>} Enregistrer le profil</Button>
            </div>
          </div>
        )}

      </div>

      {/* MODALE D'INVITATION PAR EMAIL */}
      {showInviteModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in zoom-in duration-200">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                  <div className="bg-slate-50 p-4 border-b border-slate-100 flex justify-between items-center"><h3 className="font-bold text-slate-800">Inviter un membre</h3><button onClick={() => setShowInviteModal(false)}><X size={20} className="text-slate-400"/></button></div>
                  
                  {!inviteLink ? (
                      <div className="p-6 space-y-4">
                          <div>
                              <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Email du collaborateur</label>
                              <input 
                                type="email" 
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500" 
                                placeholder="email@exemple.com" 
                                value={inviteEmail}
                                onChange={e => setInviteEmail(e.target.value)}
                              />
                          </div>

                          <div>
                              <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Rôle</label>
                              <div className="grid gap-2">
                                  {Object.values(ROLES).filter(role => role.id !== 'owner' && role.id !== 'editor').map(role => (
                                      <button 
                                        key={role.id}
                                        onClick={() => setInviteRole(role.id)}
                                        className={`flex items-start gap-3 p-3 rounded-xl border transition text-left group ${inviteRole === role.id ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-blue-300'}`}
                                      >
                                          <div className={`mt-1 w-4 h-4 rounded-full border-2 ${inviteRole === role.id ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}></div>
                                          <div>
                                              <span className="font-bold text-sm text-slate-800 block">{role.label}</span>
                                              <span className="text-xs text-slate-500">{role.desc}</span>
                                          </div>
                                      </button>
                                  ))}
                              </div>
                          </div>
                          
                          <div className="pt-2">
                              {inviteSent ? (
                                  <div className="bg-green-100 text-green-700 p-3 rounded-lg text-center text-sm font-bold flex items-center justify-center gap-2"><Check size={16}/> Invitation envoyée !</div>
                              ) : (
                                  <Button variant="primary" className="w-full justify-center" onClick={sendInvite} disabled={loading || !inviteEmail}>{loading ? <Loader2 className="animate-spin"/> : 'Envoyer l\'invitation'}</Button>
                              )}
                          </div>
                      </div>
                  ) : (
                      <div className="p-6 text-center space-y-4">
                          <h3 className="font-bold text-lg text-slate-800">Lien généré !</h3>
                          <div className="flex items-center gap-2 bg-slate-100 p-2 rounded-lg border border-slate-200"><input readOnly value={inviteLink} className="bg-transparent text-xs flex-1 outline-none text-slate-600 font-mono"/><button onClick={() => {navigator.clipboard.writeText(inviteLink); alert('Copié !')}} className="p-2 bg-white rounded shadow-sm hover:text-blue-600"><Copy size={14}/></button></div>
                          <Button variant="primary" onClick={() => {setShowInviteModal(false); setInviteLink('')}} className="w-full justify-center">Terminé</Button>
                      </div>
                  )}
              </div>
          </div>
      )}
    </div>
  );
};

export default Settings;
